import * as React from 'react';
import type { DataValue } from 'react-apollo';
import { graphql } from 'react-apollo';

import gql from 'graphql-tag';
import { isEqual } from 'lodash';
import PropTypes from 'prop-types';
import { compose, getContext } from 'recompose';

import { Typography2 } from '@coursera/cds-core';
import { Box, Col, Loading, PaginationControls, Row } from '@coursera/coursera-ui';

import { Heading, Spacer } from 'bundles/enterprise-ui/components';
import PlagiarismEmailsRow from 'bundles/internal-enterprise/components/InternalThirdPartyOrganizationPage/PlagiarismEmailsRow';
import type {
  PlagiarismEmailsQuery,
  PlagiarismEmailsQueryVariables,
  PlagiarismEmailsQuery_EnterpriseSettingsV1Resource_byOrganization_elements as Setting,
} from 'bundles/internal-enterprise/components/InternalThirdPartyOrganizationPage/__generated__/PlagiarismEmailsQuery';
import {
  PLAGIARISM_EMAIL_RECIPIENTS,
  getOrganizationLevelStringArraySettingValue,
  getProgramLevelStringArraySettingValue,
  updateEnterpriseSetting,
  updateOrganizationLevelStringArraySetting,
} from 'bundles/internal-enterprise/utils/enterpriseSettingsUtils';
import Button from 'bundles/internal-ui/components/buttons/Button';

import _t from 'i18n!nls/internal-enterprise';

const getPrograms = (data?: DataValue<PlagiarismEmailsQuery, PlagiarismEmailsQueryVariables>) =>
  data?.EnterpriseProgramsV1Resource?.byThirdPartyOrganizationId?.elements;

type PropsFromCaller = {
  thirdPartyOrganizationId: string;
};

type PropsFromGraphql = {
  data?: DataValue<PlagiarismEmailsQuery, PlagiarismEmailsQueryVariables>;
};

type PropsFromGetContext = {
  addToast: (message: string) => void;
};

type Props = PropsFromCaller & PropsFromGraphql & PropsFromGetContext;

type State = {
  isSaving: boolean;
  initialOrganizationRecipients?: string[];
  organizationRecipients?: string[];
  initialProgramRecipients?: string[][];
  programRecipients?: string[][];
  programsPage: number;
};

class PlagiarismEmails extends React.Component<Props, State> {
  state: State = {
    isSaving: false,
    programsPage: 0,
  };

  componentDidUpdate(prevProps: Props) {
    const { data, thirdPartyOrganizationId } = this.props;
    if (prevProps.data?.loading && !data?.loading) {
      const settings = data?.EnterpriseSettingsV1Resource?.byOrganization?.elements as Setting[];
      const programs = getPrograms(data);

      const orgLevel: string[] = getOrganizationLevelStringArraySettingValue(
        thirdPartyOrganizationId,
        PLAGIARISM_EMAIL_RECIPIENTS,
        settings
      );
      const programLevel = programs?.map((program) =>
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        getProgramLevelStringArraySettingValue(program!.id, PLAGIARISM_EMAIL_RECIPIENTS, settings)
      );
      this.setState({
        initialOrganizationRecipients: orgLevel,
        organizationRecipients: orgLevel,
        programRecipients: programLevel,
        initialProgramRecipients: programLevel,
      });
    }
  }

  handleSave = () => {
    const { thirdPartyOrganizationId, addToast, data } = this.props;
    const { organizationRecipients, initialOrganizationRecipients, programRecipients, initialProgramRecipients } =
      this.state;
    const programs = getPrograms(data);

    const promises: Promise<{}>[] = [];

    if (organizationRecipients && !isEqual(organizationRecipients, initialOrganizationRecipients)) {
      promises.push(
        updateOrganizationLevelStringArraySetting({
          id: thirdPartyOrganizationId,
          setting: PLAGIARISM_EMAIL_RECIPIENTS,
          strings: organizationRecipients,
        })
      );
    }

    programRecipients?.forEach((recipients, index) => {
      if (recipients && !isEqual(recipients, initialProgramRecipients?.[index])) {
        promises.push(
          updateEnterpriseSetting({
            scope: 'program!',
            id: programs?.[index]?.id,
            setting: PLAGIARISM_EMAIL_RECIPIENTS,
            value: {
              typeName: 'stringArrayContent',
              definition: {
                value: recipients,
              },
            },
          })
        );
      }
    });

    if (promises.length === 0) {
      addToast(_t('Nothing to save!'));
    } else {
      this.setState({ isSaving: true });
      Promise.all(promises)
        .then(() => {
          addToast(_t('Saved!'));
          this.setState({ isSaving: false, initialOrganizationRecipients: organizationRecipients });
        })
        .catch(() => {
          addToast(_t('Failed!'));
          this.setState({ isSaving: false });
        });
    }
  };

  render() {
    const { data } = this.props;
    const { organizationRecipients, programRecipients, isSaving, programsPage } = this.state;
    if (!data || data.loading || !organizationRecipients || !programRecipients) {
      return <Loading />;
    }
    const programs = getPrograms(data);
    const programsPerPage = 10;

    return (
      <div>
        <Spacer size={32} />
        <Box alignItems="center">
          <Heading as="h2">{_t('Plagiarism Email Recipients')}</Heading>
          <Spacer size={32} />
          <Button btnStyle="primary" text={_t('Save')} disabled={isSaving} onClick={this.handleSave} />
        </Box>
        <Spacer size={32} />
        {programs?.length === 0 && (
          <Typography2 component="p" color="supportText">
            No programs
          </Typography2>
        )}
        {programs && programs.length > 0 && (
          <div>
            <Row>
              <Col md={4} />
              <Col md={1}>
                <Typography2 component="p" color="supportText">
                  {_t('Org Admins')}
                </Typography2>
              </Col>
              <Col md={1}>
                <Typography2 component="p" color="supportText">
                  {_t('Program Admins')}
                </Typography2>
              </Col>
              <Col md={2}>
                <Typography2 component="p" color="supportText">
                  {_t('Additional User IDs')}
                </Typography2>
              </Col>
            </Row>
            <Spacer size={12} />
            <Typography2 component="p" color="supportText">
              {_t('Organization Level')}
            </Typography2>
            <PlagiarismEmailsRow
              title={<strong>{_t('All Programs')}</strong>}
              recipients={organizationRecipients}
              setRecipients={(recipients) => {
                this.setState({
                  organizationRecipients: recipients,
                });
              }}
            />
            <Spacer size={32} />
            <Typography2 component="p" color="supportText">
              {_t('Program Level')}
            </Typography2>
            {programs.slice(programsPage * programsPerPage, (programsPage + 1) * programsPerPage).map((program) => {
              if (!program) {
                return null;
              }

              const programIndex = programs.indexOf(program);

              return (
                <PlagiarismEmailsRow
                  key={program.id}
                  title={program.metadata.name}
                  // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
                  recipients={programRecipients?.[programIndex]}
                  setRecipients={(recipients: string[]) => {
                    this.setState((state) => ({
                      programRecipients: state.programRecipients?.map((_, index) => {
                        if (index === programIndex) {
                          return recipients;
                        }
                        return _;
                      }),
                    }));
                  }}
                  recipientsOverride={organizationRecipients}
                />
              );
            })}
            <Spacer size={12} />
            {programs.length > programsPerPage && (
              <PaginationControls
                pageCount={Math.ceil(programs.length / programsPerPage)}
                currentPage={programsPage + 1}
                onClickHandler={(pageNumber) => {
                  this.setState({
                    programsPage: pageNumber - 1,
                  });
                }}
                style={{ margin: 0 }}
              />
            )}
          </div>
        )}
      </div>
    );
  }
}

export const QueryForPlagiarismEmails = gql`
  query PlagiarismEmailsQuery($thirdPartyOrganizationId: String!) {
    EnterpriseSettingsV1Resource {
      byOrganization(thirdPartyOrganizationId: $thirdPartyOrganizationId, includeProgramSettings: true) {
        elements {
          id
          setting {
            ... on EnterpriseSettingsV1_booleanContentMember {
              booleanContent {
                value
              }
            }
            ... on EnterpriseSettingsV1_integerContentMember {
              integerContent {
                value
              }
            }
            ... on EnterpriseSettingsV1_stringContentMember {
              stringContent {
                value
              }
            }
            ... on EnterpriseSettingsV1_stringArrayContentMember {
              stringArrayContent {
                value
              }
            }
            ... on EnterpriseSettingsV1_stringMapContentMember {
              stringMapContent {
                value
              }
            }
          }
        }
      }
    }
    EnterpriseProgramsV1Resource {
      byThirdPartyOrganizationId(thirdPartyOrganizationId: $thirdPartyOrganizationId, limit: 9999) {
        elements {
          id
          metadata {
            name
          }
        }
      }
    }
  }
`;

export default compose<Props, PropsFromCaller>(
  graphql<PropsFromCaller, PlagiarismEmailsQuery, PlagiarismEmailsQueryVariables, PropsFromGraphql>(
    QueryForPlagiarismEmails,
    {
      skip: (props) => !props.thirdPartyOrganizationId,
      options: (props) => ({
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        variables: { thirdPartyOrganizationId: props.thirdPartyOrganizationId! },
      }),
    }
  ),
  getContext({
    addToast: PropTypes.func,
  })
)(PlagiarismEmails);
